/**
 * Eventos del contrato 3.2 de telemetría del funnel (la 3.2 solo añade claves y
 * eventos del upsell a la 3.1). Fuente única:
 * `contract/front-event-v3.catalog.json` (copia idéntica de la de docs-central,
 * L5FrontEvent y el CRM); `__tests__/contract.test.ts` compara este catálogo
 * tipado con el JSON y falla si difieren.
 *
 * Lista cerrada: cada evento con las claves exactas de su payload y el tipo de
 * cada una (`PAYLOAD_TYPES`). Antes de enviar, el tracker deja en el payload
 * solo esas claves y solo valores que cumplen su tipo (`filterPayload`): una
 * respuesta del cliente, un valor de campo o un texto de error que se colase en
 * un payload nunca sale del navegador. El front manda solo los nombres
 * canónicos 3.1; los alias de la 3 son cosa de L5FrontEvent.
 */
import type { StepId } from "./steps";
import { redactPath, redactUrl } from "./redact";

/** Versiones del sobre que acepta L5FrontEvent (`envelope.schema`). */
export const SCHEMA_VERSIONS = ["3.1", "3.2"] as const;
export type SchemaVersion = (typeof SCHEMA_VERSIONS)[number];

/** La que manda este front. */
export const SCHEMA_VERSION = "3.2" as const;

// ── Enums del contrato (el test los compara con el JSON) ─────────────────

export const STACKS = ["product", "medical", "postpayment", "other"] as const;
export type Stack = (typeof STACKS)[number];

export const PAYMENT_METHODS = ["card", "apple_pay", "google_pay"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_STAGES = ["setup", "ensure_funds"] as const;
export type PaymentStage = (typeof PAYMENT_STAGES)[number];

export const THREEDS_RESULTS = ["succeeded", "failed"] as const;
export type ThreeDsResult = (typeof THREEDS_RESULTS)[number];

export const FIELD_ERROR_TYPES = ["format", "api"] as const;
export type ValidationErrorType = (typeof FIELD_ERROR_TYPES)[number];

export const SUBMIT_ERROR_TYPES = [
  "network",
  "conflict",
  "status",
  "stripe",
  "signature",
  "proposal",
] as const;
export type SubmissionErrorType = (typeof SUBMIT_ERROR_TYPES)[number];

/** Botones de contacto: llamar, WhatsApp, «te llamamos», reservar cita o escribir. */
export const CTA_IDS = ["call", "whatsapp", "callback", "meeting", "email"] as const;
export type CtaId = (typeof CTA_IDS)[number];

/** Peticiones de ayuda hechas de verdad (no solo el clic): hoy, «te llamamos» enviado. */
export const HELP_CHANNELS = ["callback"] as const;
export type HelpChannel = (typeof HELP_CHANNELS)[number];

/** Identificadores de clic de las campañas: se dice cuál hay, nunca su valor. */
export const CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid"] as const;
export type ClickId = (typeof CLICK_IDS)[number];

/** `performance.getEntriesByType("navigation")[0].type`. */
export const NAVIGATION_TYPES = ["navigate", "reload", "back_forward", "prerender"] as const;
export type NavigationType = (typeof NAVIGATION_TYPES)[number];

export const EXIT_REASONS = ["unload", "redirect"] as const;
export type ExitReason = (typeof EXIT_REASONS)[number];

/** Adónde manda la app al cliente cuando se va de la página (`tracker.redirecting`). */
export const EXIT_DESTINATIONS = ["3ds", "signature", "velogica", "calendar", "external"] as const;
export type ExitDestination = (typeof EXIT_DESTINATIONS)[number];

/** Cómo conoce el front el lead (`lead_identified.via`). */
export const LEAD_VIA = ["created", "resumed", "login", "link", "partner", "unknown"] as const;
export type LeadVia = (typeof LEAD_VIA)[number];

export const LOGIN_METHODS = ["link", "code", "password", "google"] as const;
export type LoginMethod = (typeof LOGIN_METHODS)[number];

export const LOGIN_FAILURE_REASONS = ["invalid", "freed", "network", "unknown"] as const;
export type LoginFailureReason = (typeof LOGIN_FAILURE_REASONS)[number];

export const QUOTE_CHANGES = ["capital", "coverage", "period", "other"] as const;
export type QuoteChange = (typeof QUOTE_CHANGES)[number];

export const APP_ERROR_KINDS = ["error", "unhandled_rejection", "chunk_load"] as const;
export type AppErrorKind = (typeof APP_ERROR_KINDS)[number];

export const WEB_VITAL_NAMES = ["LCP", "FID", "CLS", "FCP", "TTFB", "INP"] as const;
export type WebVitalName = (typeof WEB_VITAL_NAMES)[number];

// Upsell (3.2).

/** El capital final frente al inicial; `same` si solo cambió la cobertura. */
export const UPSELL_DIRECTIONS = ["up", "down", "same"] as const;
export type UpsellDirection = (typeof UPSELL_DIRECTIONS)[number];

/** Cómo llegó al capital elegido: el preseleccionado, una tarjeta o el capital libre. */
export const UPSELL_VIAS = ["initial", "option", "custom"] as const;
export type UpsellVia = (typeof UPSELL_VIAS)[number];

/** Tarjeta pulsada respecto al capital inicial (`other` si no se sabe el inicial). */
export const UPSELL_POSITIONS = ["lower", "initial", "upper", "other"] as const;
export type UpsellPosition = (typeof UPSELL_POSITIONS)[number];

/** Cómo acaba un «aplicar» del capital libre. */
export const UPSELL_CUSTOM_RESULTS = [
  "applied",
  "adjusted",
  "below_min",
  "above_max",
  "invalid",
  "error",
] as const;
export type UpsellCustomResult = (typeof UPSELL_CUSTOM_RESULTS)[number];

// Sobre de cada evento.
export const ENVS = ["prd", "stg", "dev"] as const;
export type TrackingEnv = (typeof ENVS)[number];

export const MARKETS = ["ES", "IT", "FR", "AXA_WL", "SQL_ES"] as const;
export type Market = (typeof MARKETS)[number];

export const DEVICE_TYPES = ["mobile", "tablet", "desktop"] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];

/** Consentimiento de analítica de CookieYes al emitir el evento. */
export const CONSENT_STATES = ["granted", "denied", "pending"] as const;
export type ConsentState = (typeof CONSENT_STATES)[number];

// ── Tipos de valor (`types` del contrato) ────────────────────────────────

/** Nombre de campo: nunca el valor. */
export const FIELD_NAME = /^[A-Za-z][A-Za-z0-9_.]{0,63}$/;
export const SLUG = /^[a-z0-9][a-z0-9_.-]{0,63}$/;
const MAX_STRING = 255;

export const isFieldName = (value: unknown): value is string =>
  typeof value === "string" && FIELD_NAME.test(value);

export const isSlug = (value: unknown): value is string =>
  typeof value === "string" && SLUG.test(value);

/** `name0`, `idNumber2`… (un campo por beneficiario) → `name`, `idNumber`. */
export const fieldNameOf = (key: string): string => key.replace(/\d+$/, "") || key;

export type ValueType =
  | "string"
  | "id"
  | "int"
  | "number"
  | "bool"
  | "path"
  | "url"
  | "field"
  | "slug";

/** Tipo de una clave del payload: un tipo del contrato o la lista cerrada de valores. */
export type ValueSpec = ValueType | readonly string[];

// ── Payloads ─────────────────────────────────────────────────────────────

type Empty = Record<string, never>;

export interface EventPayloads {
  // sesión
  session_started: {
    entry_path: string;
    referrer: string | null;
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    utm_content: string | null;
    utm_term: string | null;
    click_id: ClickId | null;
    returning_visitor: boolean;
  };
  page_loaded: { step: StepId; navigation: NavigationType | null };
  heartbeat: { step: StepId; active_ms: number };
  tab_hidden: { step: StepId };
  tab_visible: { step: StepId };
  exit_intent_detected: { step: StepId };
  page_exited: {
    step: StepId;
    reason: ExitReason;
    destination: ExitDestination | null;
    time_on_step_ms: number;
    active_on_step_ms: number;
    last_field: string | null;
  };
  // lead y acceso
  lead_identified: { via: LeadVia };
  login_code_requested: Empty;
  login_succeeded: { method: LoginMethod };
  login_failed: { method: LoginMethod; reason: LoginFailureReason };
  // pasos
  step_viewed: { step: StepId; raw_status: string | null };
  step_completed: {
    step: StepId;
    raw_status: string | null;
    time_on_step_ms: number;
    active_on_step_ms: number;
    repeat: boolean;
  };
  step_back: { from: StepId; to: StepId };
  step_submit_failed: { step: StepId; error_type: SubmissionErrorType; code: string | null };
  // formularios
  field_invalid: {
    field: string;
    step: StepId;
    error_type: ValidationErrorType;
    code: string | null;
    attempt: number;
  };
  field_completed: {
    field: string;
    step: StepId;
    time_ms: number;
    edits: number;
    corrections: number;
  };
  // preguntas
  question_viewed: { question_id: string; stack: Stack };
  question_answered: { question_id: string; stack: Stack; time_ms: number; attempts: number };
  // precio
  quote_viewed: {
    capital: number | null;
    price: number | null;
    period: string | null;
    coverage: string | null;
  };
  quote_changed: {
    changed: QuoteChange;
    capital: number | null;
    price: number | null;
    period: string | null;
    coverage: string | null;
  };
  upsell_viewed: {
    has_promotion: boolean;
    initial_capital: number | null;
    lower_capital: number | null;
    upper_capital: number | null;
    options: number;
  };
  upsell_accepted: {
    capital: number | null;
    price: number | null;
    period: string | null;
    initial_capital: number | null;
    direction: UpsellDirection | null;
    via: UpsellVia;
    changes: number;
    stepper_clicks: number;
    time_ms: number | null;
  };
  upsell_declined: {
    initial_capital: number | null;
    via: UpsellVia;
    changes: number;
    stepper_clicks: number;
    time_ms: number | null;
  };
  upsell_option_selected: {
    capital: number | null;
    price: number | null;
    position: UpsellPosition;
  };
  upsell_custom_applied: {
    requested_capital: number | null;
    capital: number | null;
    result: UpsellCustomResult;
  };
  promo_applied: { code: string };
  promo_rejected: Empty;
  content_opened: { content_id: string; step: StepId };
  // pago
  payment_method_selected: { method: PaymentMethod };
  payment_method_submitted: { method: PaymentMethod };
  payment_method_saved: { method: PaymentMethod };
  payment_failed: {
    stage: PaymentStage;
    method: PaymentMethod | null;
    decline_code: string | null;
  };
  threeds_requested: { stage: PaymentStage };
  threeds_completed: { stage: PaymentStage; result: ThreeDsResult };
  funds_verified: Empty;
  funds_verification_pending: Empty;
  // firma
  legal_note_accepted: Empty;
  signature_shown: Empty;
  signature_completed: Empty;
  signature_expired: Empty;
  // ayuda
  cta_clicked: { cta_id: CtaId; step: StepId };
  help_requested: { step: StepId; channel: HelpChannel };
  // técnico
  app_error: { kind: AppErrorKind; name: string | null; step: StepId };
  api_slow: { endpoint: string; ms: number; step: StepId };
  web_vital: { name: WebVitalName; value: number; step: StepId };
}

export type EventName = keyof EventPayloads;

/**
 * Tipo de cada clave de cada payload, en el orden del contrato. El tipo de
 * `PAYLOAD_TYPES` obliga a que estén todas las claves de `EventPayloads` y
 * ninguna más; el test obliga a que coincida con el JSON.
 */
export const PAYLOAD_TYPES: {
  [K in EventName]: { [P in keyof EventPayloads[K]]-?: ValueSpec };
} = {
  session_started: {
    entry_path: "path",
    referrer: "url",
    utm_source: "string",
    utm_medium: "string",
    utm_campaign: "string",
    utm_content: "string",
    utm_term: "string",
    click_id: CLICK_IDS,
    returning_visitor: "bool",
  },
  page_loaded: { step: "string", navigation: NAVIGATION_TYPES },
  heartbeat: { step: "string", active_ms: "int" },
  tab_hidden: { step: "string" },
  tab_visible: { step: "string" },
  exit_intent_detected: { step: "string" },
  page_exited: {
    step: "string",
    reason: EXIT_REASONS,
    destination: EXIT_DESTINATIONS,
    time_on_step_ms: "int",
    active_on_step_ms: "int",
    last_field: "field",
  },
  lead_identified: { via: LEAD_VIA },
  login_code_requested: {},
  login_succeeded: { method: LOGIN_METHODS },
  login_failed: { method: LOGIN_METHODS, reason: LOGIN_FAILURE_REASONS },
  step_viewed: { step: "string", raw_status: "string" },
  step_completed: {
    step: "string",
    raw_status: "string",
    time_on_step_ms: "int",
    active_on_step_ms: "int",
    repeat: "bool",
  },
  step_back: { from: "string", to: "string" },
  step_submit_failed: { step: "string", error_type: SUBMIT_ERROR_TYPES, code: "id" },
  field_invalid: {
    field: "field",
    step: "string",
    error_type: FIELD_ERROR_TYPES,
    code: "slug",
    attempt: "int",
  },
  field_completed: {
    field: "field",
    step: "string",
    time_ms: "int",
    edits: "int",
    corrections: "int",
  },
  question_viewed: { question_id: "id", stack: STACKS },
  question_answered: { question_id: "id", stack: STACKS, time_ms: "int", attempts: "int" },
  quote_viewed: { capital: "number", price: "number", period: "string", coverage: "string" },
  quote_changed: {
    changed: QUOTE_CHANGES,
    capital: "number",
    price: "number",
    period: "string",
    coverage: "string",
  },
  upsell_viewed: {
    has_promotion: "bool",
    initial_capital: "number",
    lower_capital: "number",
    upper_capital: "number",
    options: "int",
  },
  upsell_accepted: {
    capital: "number",
    price: "number",
    period: "string",
    initial_capital: "number",
    direction: UPSELL_DIRECTIONS,
    via: UPSELL_VIAS,
    changes: "int",
    stepper_clicks: "int",
    time_ms: "int",
  },
  upsell_declined: {
    initial_capital: "number",
    via: UPSELL_VIAS,
    changes: "int",
    stepper_clicks: "int",
    time_ms: "int",
  },
  upsell_option_selected: { capital: "number", price: "number", position: UPSELL_POSITIONS },
  upsell_custom_applied: {
    requested_capital: "number",
    capital: "number",
    result: UPSELL_CUSTOM_RESULTS,
  },
  promo_applied: { code: "string" },
  promo_rejected: {},
  content_opened: { content_id: "slug", step: "string" },
  payment_method_selected: { method: PAYMENT_METHODS },
  payment_method_submitted: { method: PAYMENT_METHODS },
  payment_method_saved: { method: PAYMENT_METHODS },
  payment_failed: { stage: PAYMENT_STAGES, method: PAYMENT_METHODS, decline_code: "string" },
  threeds_requested: { stage: PAYMENT_STAGES },
  threeds_completed: { stage: PAYMENT_STAGES, result: THREEDS_RESULTS },
  funds_verified: {},
  funds_verification_pending: {},
  legal_note_accepted: {},
  signature_shown: {},
  signature_completed: {},
  signature_expired: {},
  cta_clicked: { cta_id: CTA_IDS, step: "string" },
  help_requested: { step: "string", channel: HELP_CHANNELS },
  app_error: { kind: APP_ERROR_KINDS, name: "slug", step: "string" },
  api_slow: { endpoint: "path", ms: "int", step: "string" },
  web_vital: { name: WEB_VITAL_NAMES, value: "number", step: "string" },
};

export const EVENT_NAMES = Object.keys(PAYLOAD_TYPES) as EventName[];

export const PAYLOAD_KEYS = EVENT_NAMES.reduce(
  (acc, name) => ({ ...acc, [name]: Object.keys(PAYLOAD_TYPES[name]) }),
  {} as { [K in EventName]: ReadonlyArray<keyof EventPayloads[K]> }
);

export const isEventName = (name: string): name is EventName =>
  Object.prototype.hasOwnProperty.call(PAYLOAD_TYPES, name);

const truncate = (value: string, max: number = MAX_STRING): string =>
  value.length > max ? value.slice(0, max) : value;

/**
 * Valor que cumple su tipo, o null. Las rutas y URL salen ya redactadas; los
 * nombres de campo y los slugs que no cumplen su forma se descartan (así un
 * texto de error que llegue como nombre de campo no sale).
 */
export const coerceValue = (spec: ValueSpec, value: string | number | boolean): unknown => {
  if (typeof spec !== "string") {
    return typeof value === "string" && spec.indexOf(value) >= 0 ? value : null;
  }
  switch (spec) {
    case "string":
      return typeof value === "boolean" ? null : truncate(String(value));
    case "id":
      if (typeof value === "number") return Number.isInteger(value) ? String(value) : null;
      return typeof value === "string" && value !== "" ? truncate(value) : null;
    case "int":
      return typeof value === "number" && Number.isFinite(value) && value >= 0
        ? Math.round(value)
        : null;
    case "number":
      return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
    case "bool":
      return typeof value === "boolean" ? value : null;
    case "path":
      return typeof value === "string" ? redactPath(value) : null;
    case "url":
      return typeof value === "string" ? redactUrl(value) : null;
    case "field":
      return isFieldName(value) ? value : null;
    case "slug":
      return isSlug(value) ? value : null;
    default:
      return null;
  }
};

/**
 * Deja en el payload solo las claves de su evento, con valores que cumplen su
 * tipo (o null). Lo que no es un valor simple (objetos, funciones…) se
 * descarta: el contrato solo lleva escalares.
 */
export const filterPayload = <K extends EventName>(
  name: K,
  payload: unknown
): EventPayloads[K] => {
  const source = (payload && typeof payload === "object" ? payload : {}) as Record<
    string,
    unknown
  >;
  const types = PAYLOAD_TYPES[name] as Record<string, ValueSpec>;
  const out: Record<string, unknown> = {};
  Object.keys(types).forEach((key) => {
    const value = source[key];
    if (value === undefined) return;
    if (value === null) {
      out[key] = null;
      return;
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      out[key] = coerceValue(types[key], value);
    }
  });
  return out as EventPayloads[K];
};

/** Un evento tal como viaja en `{"sent_at": …, "events": [...]}`. */
export interface TrackedEvent<K extends EventName = EventName> {
  client_event_id: string;
  event_name: K;
  timestamp: string;
  visitor_id: string;
  session_id: string;
  lead_ulid: string | null;
  env: TrackingEnv;
  market: Market | null;
  device_type: DeviceType;
  app_version: string | null;
  ab_variant: string | null;
  path: string;
  payload: EventPayloads[K];
  schema: SchemaVersion;
  /** Una por carga de página (pestaña): agrupa sus eventos. */
  page_id: string;
  /** 1, 2, 3… dentro de `page_id`: un hueco es un evento perdido. */
  seq: number;
  consent: ConsentState | null;
}

/** Claves del sobre, en el orden del contrato (el test las compara con el JSON). */
export const ENVELOPE_KEYS: ReadonlyArray<keyof TrackedEvent> = [
  "client_event_id",
  "event_name",
  "timestamp",
  "visitor_id",
  "session_id",
  "lead_ulid",
  "env",
  "market",
  "device_type",
  "app_version",
  "ab_variant",
  "path",
  "payload",
  "schema",
  "page_id",
  "seq",
  "consent",
];
